import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { getData } from 'services/api';
import { useToastNotification } from 'hooks/useToastNotification';
import { usePreviewTicketImport, useUpdateMapping } from '../../hooks';
import { useWizard } from '../../store';
import { PreviewRow } from '../../types';
import { handleFileMissingError } from '../../utils';
import {
	Button,
	Card,
	CardDescription,
	CardTitle,
	ErrorPill,
	Footer,
	Label,
	Row,
	ScrollContainer,
	Select,
	Stat,
	SuccessPill,
	Table,
	WarningPill,
} from '../../TicketImporter.styles';

interface ApiTicketType {
	id?: number;
	ID?: number;
	name: string;
}

interface ApiEventWithTT {
	id?: number;
	ID?: number;
	name: string;
	ticket_types?: ApiTicketType[];
}

const ttID = (tt: ApiTicketType): number | undefined => tt.id ?? tt.ID;

// Errors thrown by the backend that this step can resolve.
// Matches both straight and curly quotes around the ticket-type / event names.
const TT_CONFLICT_REGEX = /ticket type ["“']([^"”']+)["”'] not found in event ["“']([^"”']+)["”']/;

interface Conflict {
	eventId: number;
	eventName: string;
	ttName: string;
	rowCount: number;
}

interface ErrorBucket {
	pattern: string;
	count: number;
	sampleRows: number[];
}

interface Diagnostics {
	totalRows: number;
	rowsWithErrors: number;
	rowsWithoutEvent: number;
	rowsMissingTT: number;
	buckets: ErrorBucket[];
	conflicts: Conflict[];
}

// Strip variable bits from an error string so we can group similar errors.
const normalizeError = (e: string): string =>
	e
		.replace(/"([^"]+)"/g, '"…"')
		.replace(/['"”'"„«»]+([^"”'"„«»]+)['"”'"„«»]+/g, '"…"')
		.replace(/#\d+/g, '#N')
		.replace(/\b\d{4}-\d{2}-\d{2}\b/g, 'YYYY-MM-DD');

const analyzePreview = (rows: PreviewRow[]): Diagnostics => {
	const conflicts = new Map<string, Conflict>();
	const buckets = new Map<string, ErrorBucket>();
	let rowsWithErrors = 0;
	let rowsWithoutEvent = 0;
	let rowsMissingTT = 0;

	for (const r of rows) {
		if (r.errors && r.errors.length > 0) rowsWithErrors += 1;
		if (!r.event_id) rowsWithoutEvent += 1;
		if (!r.ticket_type_id) rowsMissingTT += 1;

		for (const err of r.errors || []) {
			const norm = normalizeError(err);
			const existing = buckets.get(norm);
			if (existing) {
				existing.count += 1;
				if (existing.sampleRows.length < 5) existing.sampleRows.push(r.row_index);
			} else {
				buckets.set(norm, { pattern: norm, count: 1, sampleRows: [r.row_index] });
			}

			const m = err.match(TT_CONFLICT_REGEX);
			if (m && r.event_id) {
				const ttName = m[1];
				const key = `${r.event_id}|${ttName}`;
				const cExisting = conflicts.get(key);
				if (cExisting) cExisting.rowCount += 1;
				else
					conflicts.set(key, {
						eventId: r.event_id,
						eventName: r.event_name || '(unknown)',
						ttName,
						rowCount: 1,
					});
			}
		}
	}

	return {
		totalRows: rows.length,
		rowsWithErrors,
		rowsWithoutEvent,
		rowsMissingTT,
		buckets: Array.from(buckets.values()).sort((a, b) => b.count - a.count),
		conflicts: Array.from(conflicts.values()).sort(
			(a, b) => a.eventName.localeCompare(b.eventName) || a.ttName.localeCompare(b.ttName)
		),
	};
};

export const ValueMappingStep = (): JSX.Element => {
	const history = useHistory();
	const { pathname } = useLocation();
	const { state, dispatch } = useWizard();
	const { showNotification, TYPES } = useToastNotification();

	const isFixed = state.scopeMode === 'fixed';

	// ── fixed scope: existing behaviour ────────────────────────────────────────
	const ttColumn = state.mapping.ticket_type_name;
	const uniqueTTs = useMemo(() => {
		if (!ttColumn || !state.upload) return [];
		return state.upload.unique_ticket_types || [];
	}, [state.upload, ttColumn]);

	const { data: fixedEvent } = useQuery<ApiEventWithTT>({
		queryKey: ['ticket-importer-event-tts', state.fixedEventId],
		enabled: isFixed && state.fixedEventId !== null,
		queryFn: () => getData(`events/${state.fixedEventId}`),
	});

	const ttOptionsFixed = fixedEvent?.ticket_types ?? [];

	const [globalMapping, setGlobalMapping] = useState<Record<string, number>>(state.valueMapping.ticket_type || {});

	// ── per-row scope: detect conflicts via preview ────────────────────────────
	const {
		mutate: runPreview,
		data: preview,
		isLoading: previewing,
	} = usePreviewTicketImport(state.importId as number, {
		onError: (err: any) => {
			if (
				handleFileMissingError(err, {
					notify: showNotification,
					notifyType: TYPES,
					reset: () => dispatch({ type: 'reset' }),
					navigate: (p) => history.replace(p),
					basePath: pathname,
				})
			)
				return;
			const msg = err?.response?.data?.error || 'Preview failed';
			showNotification({ type: TYPES.ERROR, message: msg });
		},
	});

	// Always re-run preview when entering the step (per-row mode). Cached previews
	// from earlier in the wizard might be stale relative to the current mapping.
	useEffect(() => {
		if (!isFixed && state.importId) {
			runPreview();
		}
	}, [isFixed, state.importId, runPreview]);

	const effectivePreview = preview || state.preview;

	const diagnostics = useMemo<Diagnostics | null>(() => {
		if (isFixed || !effectivePreview) return null;
		return analyzePreview(effectivePreview.rows);
	}, [isFixed, effectivePreview]);

	const conflicts = diagnostics?.conflicts ?? [];

	const [perEventMapping, setPerEventMapping] = useState<Record<number, Record<string, number>>>(
		state.valueMapping.ticket_type_by_event || {}
	);

	// Events whose ticket types we need to load: anything that has a live
	// conflict from the preview, PLUS anything the user has already mapped
	// (so the "previously mapped" rows can render their dropdowns instead
	// of being stuck on "— pick a ticket type —" with an empty list).
	const eventsToFetchTTs = useMemo(() => {
		const ids = new Set<number>();
		for (const c of conflicts) ids.add(c.eventId);
		for (const k of Object.keys(perEventMapping)) ids.add(Number(k));
		return Array.from(ids);
	}, [conflicts, perEventMapping]);

	// Fetch ticket types for every relevant event, in parallel.
	const eventQueries = useQueries({
		queries: eventsToFetchTTs.map((id) => ({
			queryKey: ['ticket-importer-event-tts', id],
			queryFn: (): Promise<ApiEventWithTT> => getData(`events/${id}`),
		})),
	});

	const eventTTsById = useMemo(() => {
		const m: Record<number, ApiTicketType[]> = {};
		eventQueries.forEach((q, i) => {
			const id = eventsToFetchTTs[i];
			m[id] = q.data?.ticket_types ?? [];
		});
		return m;
	}, [eventQueries, eventsToFetchTTs]);

	// Cache event names so the "previously mapped" rows can show the human
	// name instead of a bare "Event #N". Sourced from the live preview when
	// the event still has a conflict, otherwise from the parallel TT fetch.
	const eventNamesById = useMemo(() => {
		const m: Record<number, string> = {};
		for (const c of conflicts) m[c.eventId] = c.eventName;
		eventQueries.forEach((q, i) => {
			const id = eventsToFetchTTs[i];
			if (q.data?.name && !m[id]) m[id] = q.data.name;
		});
		return m;
	}, [conflicts, eventQueries, eventsToFetchTTs]);

	const setPerEvent = (eventId: number, ttName: string, ttId: number | null) => {
		setPerEventMapping((prev) => {
			const next = { ...prev };
			const sub = { ...(next[eventId] || {}) };
			if (ttId === null) delete sub[ttName];
			else sub[ttName] = ttId;
			next[eventId] = sub;
			if (Object.keys(sub).length === 0) delete next[eventId];
			return next;
		});
	};

	// Names the user explicitly opted out of importing. Stored as a per-event
	// set of CSV TT names. When toggled on we also drop the regular mapping
	// for the same (event, name) so the two pieces of state can't disagree.
	const [skipByEvent, setSkipByEvent] = useState<Record<number, string[]>>(
		state.valueMapping.ticket_type_skip_by_event || {}
	);
	const isSkipped = (eventId: number, ttName: string): boolean => {
		const list = skipByEvent[eventId];
		return Array.isArray(list) && list.includes(ttName);
	};
	const setSkip = (eventId: number, ttName: string, skip: boolean) => {
		setSkipByEvent((prev) => {
			const next = { ...prev };
			const current = new Set(next[eventId] || []);
			if (skip) current.add(ttName);
			else current.delete(ttName);
			if (current.size === 0) delete next[eventId];
			else next[eventId] = Array.from(current);
			return next;
		});
		if (skip) {
			// Skipping means we discard any explicit ticket type mapping for
			// this row so the two pieces of state stay consistent.
			setPerEvent(eventId, ttName, null);
		}
	};

	// ── persistence ────────────────────────────────────────────────────────────
	const { mutate, isLoading: saving } = useUpdateMapping(state.importId as number, {
		onSuccess: () => {
			dispatch({
				type: 'patch',
				payload: {
					valueMapping: {
						ticket_type: isFixed ? globalMapping : state.valueMapping.ticket_type,
						ticket_type_by_event: isFixed ? state.valueMapping.ticket_type_by_event : perEventMapping,
						ticket_type_skip_by_event: isFixed ? state.valueMapping.ticket_type_skip_by_event : skipByEvent,
					},
				},
			});
			history.push(`${pathname}#7`);
		},
		onError: () => showNotification({ type: TYPES.ERROR, message: 'Could not save value mapping' }),
	});

	const onContinue = () => {
		// Preserve event mapping (set in step 4) when saving TT mappings here,
		// otherwise the resolver loses the per-row event resolution.
		const eventMap = state.eventMapping;
		if (isFixed) {
			mutate({ value_mapping: { ticket_type: globalMapping, event: eventMap } });
		} else {
			mutate({
				value_mapping: {
					ticket_type_by_event: perEventMapping,
					ticket_type_skip_by_event: skipByEvent,
					event: eventMap,
				},
			});
		}
	};

	const setGlobal = (csvValue: string, ttId: number | null) => {
		setGlobalMapping((prev) => {
			const next = { ...prev };
			if (ttId === null) delete next[csvValue];
			else next[csvValue] = ttId;
			return next;
		});
	};

	// Union the unresolved conflicts from the latest preview with the entries
	// the user has already mapped in this session (or in a prior session that
	// got persisted to value_mapping.ticket_type_by_event). Without this, the
	// step would hide already-resolved rows and the user couldn't change a
	// previous choice without restarting the import.
	const conflictsByEvent = useMemo(() => {
		const byEvent: Record<number, Conflict[]> = {};
		const seen = new Set<string>();
		const addConflict = (c: Conflict) => {
			const key = `${c.eventId}|${c.ttName}`;
			if (seen.has(key)) return;
			seen.add(key);
			if (!byEvent[c.eventId]) byEvent[c.eventId] = [];
			byEvent[c.eventId].push(c);
		};
		// 1. Live conflicts from the latest preview (highest signal — these
		//    are the rows that still fail server-side).
		for (const c of conflicts) addConflict(c);
		// 2. Anything the user already chose, so they can review or undo it.
		for (const [eventIdStr, byName] of Object.entries(perEventMapping)) {
			const eventId = Number(eventIdStr);
			for (const ttName of Object.keys(byName)) {
				addConflict({
					eventId,
					ttName,
					eventName: eventNamesById[eventId] || `Event #${eventId}`,
					rowCount: 0, // we don't know — preview no longer flags it
				});
			}
		}
		// 3. Skipped TTs: same idea — keep them visible so the user can untick.
		for (const [eventIdStr, names] of Object.entries(skipByEvent)) {
			const eventId = Number(eventIdStr);
			for (const ttName of names) {
				addConflict({
					eventId,
					ttName,
					eventName: eventNamesById[eventId] || `Event #${eventId}`,
					rowCount: 0,
				});
			}
		}
		return byEvent;
	}, [conflicts, perEventMapping, skipByEvent, eventNamesById]);

	const allResolved = useMemo(
		() =>
			conflicts.every((c) => {
				const list = skipByEvent[c.eventId];
				const skipped = Array.isArray(list) && list.includes(c.ttName);
				return skipped || (perEventMapping[c.eventId] && perEventMapping[c.eventId][c.ttName]);
			}),
		[conflicts, perEventMapping, skipByEvent]
	);

	// ── render ─────────────────────────────────────────────────────────────────
	if (isFixed) {
		return (
			<>
				<Card>
					<CardTitle>4. Ticket type mapping</CardTitle>
					<CardDescription>
						Map each unique value of the CSV&apos;s ticket-type column to one of the event&apos;s ticket types. Leave
						empty to let the backend match by name.
					</CardDescription>

					{uniqueTTs.length === 0 && (
						<p style={{ color: '#9ca3af', fontSize: 14 }}>
							No ticket type column mapped, or the CSV has no values for it.
						</p>
					)}

					{uniqueTTs.map((csvValue) => (
						<Row key={csvValue}>
							<Label>
								<strong>{csvValue}</strong>
								<Select
									value={globalMapping[csvValue] ?? ''}
									onChange={(e) => setGlobal(csvValue, e.target.value ? Number(e.target.value) : null)}
								>
									<option value="">— match by name (default) —</option>
									{ttOptionsFixed
										.map((tt) => ({ tt, id: ttID(tt) }))
										.filter(({ id }) => typeof id === 'number')
										.map(({ tt, id }) => (
											<option key={id} value={id}>
												{tt.name}
											</option>
										))}
								</Select>
							</Label>
						</Row>
					))}
				</Card>

				<Footer>
					<Button variant="secondary" onClick={() => history.push(`${pathname}#5`)}>
						← Back
					</Button>
					<Button onClick={onContinue} disabled={saving}>
						Continue →
					</Button>
				</Footer>
			</>
		);
	}

	// per-row scope
	return (
		<>
			<Card>
				<CardTitle>4. Ticket type mapping</CardTitle>
				<CardDescription>
					Per-row scope is selected. The backend matches ticket type names per event automatically; we only ask you to
					resolve cases where a CSV name doesn&apos;t match any ticket type in the target event.
				</CardDescription>

				{previewing && <p>Scanning rows for conflicts… (this can take a few seconds for large files)</p>}

				{!previewing && diagnostics && (
					<div style={{ marginBottom: 20 }}>
						<Row>
							<Stat>
								<span className="label">Rows scanned</span>
								<span className="value">{diagnostics.totalRows.toLocaleString()}</span>
							</Stat>
							<Stat>
								<span className="label">Rows with errors</span>
								<span className="value" style={{ color: diagnostics.rowsWithErrors > 0 ? '#991b1b' : '#065f46' }}>
									{diagnostics.rowsWithErrors.toLocaleString()}
								</span>
							</Stat>
							<Stat>
								<span className="label">No event resolved</span>
								<span className="value" style={{ color: diagnostics.rowsWithoutEvent > 0 ? '#991b1b' : '#065f46' }}>
									{diagnostics.rowsWithoutEvent.toLocaleString()}
								</span>
							</Stat>
							<Stat>
								<span className="label">No ticket type resolved</span>
								<span className="value" style={{ color: diagnostics.rowsMissingTT > 0 ? '#991b1b' : '#065f46' }}>
									{diagnostics.rowsMissingTT.toLocaleString()}
								</span>
							</Stat>
							<Stat>
								<span className="label">TT conflicts</span>
								<span className="value" style={{ color: diagnostics.conflicts.length > 0 ? '#92400e' : '#065f46' }}>
									{diagnostics.conflicts.length}
								</span>
							</Stat>
						</Row>

						{diagnostics.rowsWithErrors === 0 && (
							<p style={{ color: '#065f46', fontSize: 14 }}>
								<SuccessPill>All clear</SuccessPill> No errors detected — every row resolved cleanly. You can continue.
							</p>
						)}

						{diagnostics.buckets.length > 0 && (
							<>
								<h3 style={{ fontSize: 14, fontWeight: 600, margin: '20px 0 8px' }}>Error breakdown</h3>
								<p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 8px' }}>
									Errors grouped by pattern. Use this to spot mapping problems beyond ticket-type conflicts (missing
									events, bad date formats, invalid seats, etc.). The dropdowns below only resolve TT conflicts — other
									errors must be fixed by going back to earlier steps.
								</p>
								<ScrollContainer style={{ maxHeight: 240 }}>
									<Table>
										<thead>
											<tr>
												<th style={{ width: 80 }}>Count</th>
												<th>Pattern</th>
												<th>Sample row #</th>
											</tr>
										</thead>
										<tbody>
											{diagnostics.buckets.map((b) => (
												<tr key={b.pattern}>
													<td>
														{b.pattern.match(TT_CONFLICT_REGEX) ? (
															<WarningPill>{b.count}</WarningPill>
														) : (
															<ErrorPill>{b.count}</ErrorPill>
														)}
													</td>
													<td style={{ fontFamily: 'monospace', fontSize: 12 }}>{b.pattern}</td>
													<td style={{ color: '#6b7280', fontSize: 12 }}>{b.sampleRows.join(', ')}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</ScrollContainer>
							</>
						)}

						{diagnostics.conflicts.length === 0 && diagnostics.rowsWithErrors > 0 && (
							<div
								style={{
									background: '#fef3c7',
									border: '1px solid #fbbf24',
									borderRadius: 8,
									padding: 12,
									marginTop: 16,
									fontSize: 13,
								}}
							>
								<strong>No ticket-type conflicts to resolve here</strong>, but{' '}
								{diagnostics.rowsWithErrors.toLocaleString()} row(s) failed for other reasons. Go back to fix
								mappings/options, or continue to the preview step to see row-level details.
							</div>
						)}
					</div>
				)}

				{!previewing &&
					Object.entries(conflictsByEvent).map(([eventIdStr, group]) => {
						const eventId = Number(eventIdStr);
						const tts = eventTTsById[eventId] || [];
						return (
							<div key={eventId} style={{ marginBottom: 20 }}>
								<h3 style={{ fontSize: 16, fontWeight: 600, margin: '12px 0 8px' }}>
									{eventNamesById[eventId] || group[0].eventName}{' '}
									<small style={{ color: '#9ca3af' }}>(#{eventId})</small>
								</h3>
								{group.map((c) => {
									const skipped = isSkipped(eventId, c.ttName);
									return (
										<Row key={`${eventId}-${c.ttName}`}>
											<Label>
												<span>
													<strong>{c.ttName}</strong>{' '}
													{skipped ? (
														<WarningPill>skipped</WarningPill>
													) : c.rowCount > 0 ? (
														<small style={{ color: '#9ca3af' }}>
															({c.rowCount} row{c.rowCount > 1 ? 's' : ''})
														</small>
													) : (
														<small style={{ color: '#6366f1' }}>previously mapped</small>
													)}
												</span>
												<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
													<Select
														value={perEventMapping[eventId]?.[c.ttName] ?? ''}
														onChange={(e) =>
															setPerEvent(eventId, c.ttName, e.target.value ? Number(e.target.value) : null)
														}
														disabled={skipped}
														style={{ flex: 1 }}
													>
														<option value="">— pick a ticket type —</option>
														{tts
															.map((tt) => ({ tt, id: ttID(tt) }))
															.filter(({ id }) => typeof id === 'number')
															.map(({ tt, id }) => (
																<option key={id} value={id}>
																	{tt.name}
																</option>
															))}
													</Select>
													<label
														style={{
															display: 'flex',
															alignItems: 'center',
															gap: 4,
															fontSize: 13,
															color: '#6b7280',
															whiteSpace: 'nowrap',
															cursor: 'pointer',
														}}
														title="Skip every row with this ticket type for this event — no ticket will be created."
													>
														<input
															type="checkbox"
															checked={skipped}
															onChange={(e) => setSkip(eventId, c.ttName, e.target.checked)}
														/>
														Skip
													</label>
												</div>
											</Label>
										</Row>
									);
								})}
							</div>
						);
					})}
			</Card>

			<Footer>
				<Button variant="secondary" onClick={() => history.push(`${pathname}#5`)}>
					← Back
				</Button>
				<div style={{ display: 'flex', gap: 8 }}>
					<Button variant="secondary" onClick={() => runPreview()} disabled={previewing}>
						Re-scan
					</Button>
					<Button onClick={onContinue} disabled={saving || (conflicts.length > 0 && !allResolved)}>
						{conflicts.length > 0
							? allResolved
								? 'Save and continue →'
								: 'Resolve all conflicts to continue'
							: 'Continue →'}
					</Button>
				</div>
			</Footer>
		</>
	);
};
